import { useSyncExternalStore } from 'react';
import {
  formatShortcut,
  type CommandRegistry,
  type CommandShortcut,
} from '../lib/command-registry';

/** React subscription to the CommandRegistry's mutation stream.
 *  Returns the registry's monotonic version stamp — components that
 *  read effective shortcuts via this hook re-render whenever the
 *  registry mutates (register / unregister / override change).
 *
 *  Built on `useSyncExternalStore` so React's concurrent renderer
 *  sees a consistent snapshot across the commit (no tearing).
 *
 *  The returned number itself is meaningless — callers use it as a
 *  dependency or just include it in the call (its presence in the
 *  render path is enough to trigger re-renders). */
export function useCommandRegistryVersion(registry: CommandRegistry): number {
  return useSyncExternalStore(
    (callback) => registry.subscribe(callback),
    () => registry.getVersion(),
    // SSR snapshot — never reached in the browser-only image editor
    // but keeps the API contract honest.
    () => 0,
  );
}

/** Reactive read of a single command's effective shortcuts. Used
 *  by ToolPalette / IconLegendDialog / CommandPalette to keep
 *  shortcut hints in sync with the user's keyboard overrides.
 *
 *  Returns an empty array when the command id is unknown or has no
 *  binding — callers branch on `.length === 0` to render an
 *  "unassigned" placeholder or omit the hint entirely. */
export function useEffectiveShortcuts(
  registry: CommandRegistry,
  commandId: string,
): readonly CommandShortcut[] {
  // Force re-render on registry mutation, then re-read at render.
  useCommandRegistryVersion(registry);
  return registry.getEffectiveShortcuts(commandId);
}

/** Reactive formatted-string version of the first effective
 *  shortcut, with platform-aware glyphs. Empty string when the
 *  command has no binding — callers can drop the parenthetical
 *  in tooltips like "Crop (C)" → "Crop". */
export function useFormattedShortcut(
  registry: CommandRegistry,
  commandId: string,
  isMac: boolean,
): string {
  const shortcuts = useEffectiveShortcuts(registry, commandId);
  if (shortcuts.length === 0) return '';
  return formatShortcut(shortcuts[0], isMac);
}

/** Batch variant — looks up the formatted shortcut for each of
 *  several commands behind a SINGLE registry subscription. Use
 *  this in components that need many tooltips (e.g. the toolbar
 *  with ~12 button hints) instead of calling
 *  `useFormattedShortcut` once per id, which would set up that
 *  many separate `useSyncExternalStore` subscriptions and notify
 *  each one on every registry mutation. The returned object is
 *  keyed by commandId. */
export function useFormattedShortcuts<T extends readonly string[]>(
  registry: CommandRegistry,
  commandIds: T,
  isMac: boolean,
): Record<T[number], string> {
  useCommandRegistryVersion(registry);
  const out = {} as Record<string, string>;
  for (const id of commandIds) {
    const shortcuts = registry.getEffectiveShortcuts(id);
    out[id] = shortcuts.length === 0 ? '' : formatShortcut(shortcuts[0], isMac);
  }
  return out as Record<T[number], string>;
}
